import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Calendar = ({ maintenances, onMaintenanceClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert to Monday = 0, Sunday = 6
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  // Get number of days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get maintenances for a specific date
  const getMaintenancesForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return maintenances.filter(m => {
      if (!m.scheduledDate) return false;
      const maintenanceDate = new Date(m.scheduledDate).toISOString().split('T')[0];
      return maintenanceDate === dateStr;
    });
  };

  // Check if date is today
  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  // Navigate months
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-24 bg-gray-50 border border-gray-200"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayMaintenances = getMaintenancesForDate(day);
      const today = isToday(day);

      days.push(
        <motion.div
          key={day}
          className={`min-h-24 border border-gray-200 p-2 ${
            today ? 'bg-red-50 border-red-300' : 'bg-white hover:bg-gray-50'
          } transition-colors cursor-pointer`}
          whileHover={{ scale: 1.02 }}
        >
          <div className={`text-sm font-semibold mb-1 ${today ? 'text-red-600' : 'text-gray-700'}`}>
            {day}
            {today && <span className="ml-1 text-xs">(Aujourd'hui)</span>}
          </div>
          <div className="space-y-1">
            {dayMaintenances.slice(0, 3).map((maintenance, idx) => {
              const statusColors = {
                pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
                in_progress: 'bg-purple-100 text-purple-800 border-purple-300',
                completed: 'bg-green-100 text-green-800 border-green-300',
                cancelled: 'bg-gray-100 text-gray-800 border-gray-300',
              };

              return (
                <motion.div
                  key={idx}
                  onClick={() => onMaintenanceClick(maintenance)}
                  className={`text-xs p-1 rounded border cursor-pointer ${
                    statusColors[maintenance.status] || 'bg-gray-100 text-gray-800'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="font-semibold truncate">{maintenance.clientName}</div>
                  <div className="text-[10px] truncate">
                    {maintenance.equipmentLabel || maintenance.serialNumber || maintenance.equipmentName}
                  </div>
                </motion.div>
              );
            })}
            {dayMaintenances.length > 3 && (
              <div className="text-xs text-gray-600 font-medium">
                +{dayMaintenances.length - 3} autre(s)
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    return days;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Aujourd'hui
          </button>
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></div>
          <span>En attente</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
          <span>Planifiée</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-purple-100 border border-purple-300 rounded"></div>
          <span>En cours</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
          <span>Terminée</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0">
        {/* Day headers */}
        {dayNames.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-semibold text-gray-700 py-2 bg-gray-100 border border-gray-200"
          >
            {day}
          </div>
        ))}
        {/* Calendar days */}
        {generateCalendarDays()}
      </div>
    </div>
  );
};

export default Calendar;

